import { useState, useEffect, useRef, useCallback } from 'react';
import { Usd, PriceCaption } from '@/utils/CurrencyContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Play, Pause, Volume2, VolumeX, Maximize, SkipBack, SkipForward, Settings, CheckCircle, Loader2, AlertTriangle, FileText, Video, Star, Trophy, Medal, Eye, Music, Download, Search, Lock, ShoppingCart } from 'lucide-react';
import { motion } from 'framer-motion';
import { getPresignedDownloadUrl, extractFileKey, isPublicAsset, getPublicUrl } from '@/utils/s3Upload';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

// Helper to get proper URL for R2 files
const getR2MediaUrl = async (fileUrl, courseId) => {
  // Check if it's an R2 URL
  const isR2Url = fileUrl.includes('.r2.cloudflarestorage.com') || fileUrl.includes('.r2.dev');
  
  if (isR2Url) {
    const fileKey = extractFileKey(fileUrl);
    if (fileKey) {
      // Check if it's a public asset
      if (isPublicAsset(fileKey)) {
        return getPublicUrl(fileKey);
      }
      // Get presigned URL for private content
      try {
        const result = await getPresignedDownloadUrl(fileKey, courseId);
        return result.download_url;
      } catch (error) {
        console.error('Failed to get presigned URL:', error);
        return null;
      }
    }
  }
  
  // Legacy: Handle old /api/files/ URLs
  if (fileUrl.startsWith('/') || !fileUrl.includes('://')) {
    const token = localStorage.getItem('token');
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileUrl}?auth=${token}`;
  }
  
  return fileUrl;
};

const LessonViewPage = () => {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [lesson, setLesson] = useState(null);
  const [course, setCourse] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [videoQuality, setVideoQuality] = useState('auto');
  const [isCompleting, setIsCompleting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [openedFiles, setOpenedFiles] = useState([]);  // Per-user tracking of opened files
  const [fileSearchQuery, setFileSearchQuery] = useState('');  // Per-user file search filter
  const [mediaUrl, setMediaUrl] = useState(null);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  // Lesson purchase state
  const [hasLessonAccess, setHasLessonAccess] = useState(true); // Default true for backwards compat
  const [isLessonPurchasable, setIsLessonPurchasable] = useState(false);
  const [lessonPrice, setLessonPrice] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  // Declaration state for lesson purchase
  const [courseDeclaration, setCourseDeclaration] = useState(null);
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [isAcceptingDeclaration, setIsAcceptingDeclaration] = useState(false);
  const [hasAcceptedDeclaration, setHasAcceptedDeclaration] = useState(false);
  const controlsTimeout = useRef(null);

  useEffect(() => {
    fetchLessonData();
    return () => {
      if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
    };
  }, [courseId, lessonId]);

  // Load media URL when lesson changes
  useEffect(() => {
    if (lesson?.video_url) {
      loadMediaUrl();
    }
  }, [lesson?.video_url, lesson?.video_type]);

  const loadMediaUrl = async () => {
    if (!lesson?.video_url) {
      setMediaUrl(null);
      return;
    }

    const videoUrl = lesson.video_url;

    // Check if it's a YouTube video
    if (lesson.video_type === 'youtube') {
      const videoId = videoUrl.includes('youtube.com') 
        ? new URL(videoUrl).searchParams.get('v')
        : videoUrl.split('/').pop();
      setMediaUrl({ type: 'youtube', id: videoId });
      return;
    }

    // Check if it's an R2 URL (contains .r2.cloudflarestorage.com or .r2.dev)
    const isR2Url = videoUrl.includes('.r2.cloudflarestorage.com') || videoUrl.includes('.r2.dev');
    
    if (isR2Url) {
      setIsLoadingMedia(true);
      try {
        // Extract the file key from the R2 URL
        const fileKey = extractFileKey(videoUrl);
        if (fileKey) {
          // Get presigned URL for private content
          const result = await getPresignedDownloadUrl(fileKey, courseId);
          const isPdf = videoUrl.endsWith('.pdf') || fileKey.endsWith('.pdf');
          setMediaUrl({
            type: isPdf ? 'pdf' : 'uploaded',
            url: result.download_url
          });
        } else {
          // Fallback: use the URL directly if we can't extract the key
          const isPdf = videoUrl.endsWith('.pdf');
          setMediaUrl({ type: isPdf ? 'pdf' : 'uploaded', url: videoUrl });
        }
      } catch (error) {
        console.error('Failed to get presigned URL:', error);
        toast.error('Failed to load media. Please try again.');
        setMediaUrl(null);
      } finally {
        setIsLoadingMedia(false);
      }
      return;
    }

    // Legacy: Handle old /api/files/ URLs (for backwards compatibility)
    if (videoUrl.startsWith('/') || !videoUrl.includes('://')) {
      const token = localStorage.getItem('token');
      const isPdf = videoUrl.endsWith('.pdf');
      setMediaUrl({
        type: isPdf ? 'pdf' : 'uploaded',
        url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${videoUrl}?auth=${token}`
      });
      return;
    }

    // Direct URL (external video or already full URL)
    const isPdf = videoUrl.endsWith('.pdf');
    setMediaUrl({ type: isPdf ? 'pdf' : 'uploaded', url: videoUrl });
  };

  const fetchLessonData = async () => {
    try {
      const courseRes = await api.get(`/courses/${courseId}`);
      setCourse(courseRes.data.course || courseRes.data);
      
      // Extract opened_files from enrollment data
      const enrollmentOpenedFiles = courseRes.data.enrollment?.opened_files || [];
      setOpenedFiles(enrollmentOpenedFiles);
      
      // Check if enrollment has expired
      if (courseRes.data.is_expired) {
        setIsExpired(true);
        toast.error('Your course access has expired');
        return;
      }
      
      const foundLesson = courseRes.data.lessons?.find(l => l.id === lessonId);
      if (foundLesson) {
        setLesson(foundLesson);
        
        // Check lesson access from the response
        setHasLessonAccess(foundLesson.has_access !== false);
        setIsLessonPurchasable(foundLesson.is_purchasable || false);
        setLessonPrice(foundLesson.price);
        
        // Fetch declaration for this course (needed for lesson purchases)
        fetchCourseDeclaration();
      } else {
        toast.error('Lesson not found');
        navigate(`/courses/${courseId}`);
      }
    } catch (error) {
      toast.error('Failed to load lesson');
      navigate(`/courses/${courseId}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch course declaration for lesson purchase
  const fetchCourseDeclaration = async () => {
    try {
      const response = await api.get(`/courses/${courseId}/declaration/active`);
      if (response.data.has_declaration) {
        setCourseDeclaration(response.data.declaration);
        setHasAcceptedDeclaration(response.data.has_accepted || false);
      }
    } catch (error) {
      // No declaration or error - that's fine
      setCourseDeclaration(null);
    }
  };

  // Accept declaration for lesson purchase
  const handleAcceptDeclaration = async () => {
    if (!courseDeclaration) return;
    
    setIsAcceptingDeclaration(true);
    try {
      await api.post(`/declarations/${courseDeclaration.id}/accept`);
      setHasAcceptedDeclaration(true);
      setShowDeclarationModal(false);
      toast.success('Declaration accepted');
      
      // Now proceed with the purchase
      proceedWithLessonPurchase();
    } catch (error) {
      toast.error('Failed to accept declaration');
    } finally {
      setIsAcceptingDeclaration(false);
    }
  };

  // Actual lesson purchase after declaration check
  const proceedWithLessonPurchase = async () => {
    setIsPurchasing(true);
    try {
      const response = await api.post(`/payments/paypal/lesson/create-order?lesson_id=${lessonId}`);
      
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      } else {
        toast.error('Failed to create payment order');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to initiate purchase';
      toast.error(message);
    } finally {
      setIsPurchasing(false);
    }
  };

  const markLessonComplete = async () => {
    if (isCompleting || isCompleted) return;
    
    // Can't complete if no access
    if (!hasLessonAccess) {
      toast.error('You need to unlock this lesson first');
      return;
    }
    
    setIsCompleting(true);
    try {
      const response = await api.post(`/lessons/${lessonId}/complete`);
      setIsCompleted(true);
      
      // Show points earned
      if (response.data.points_earned > 0) {
        toast.success(
          <div className="flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500" />
            <span>+{response.data.points_earned} points earned!</span>
          </div>,
          { duration: 4000 }
        );
      } else {
        toast.success('Lesson marked as complete!');
      }
      
      // Show badges earned
      if (response.data.new_badges && response.data.new_badges.length > 0) {
        setTimeout(() => {
          response.data.new_badges.forEach((badge, idx) => {
            setTimeout(() => {
              toast.success(
                <div className="flex items-center gap-2">
                  <Medal className="h-5 w-5 text-orange-500" />
                  <span>Badge Earned: {badge.name}!</span>
                </div>,
                { duration: 5000 }
              );
            }, idx * 1000);
          });
        }, 1500);
      }
      
      // Show course completion bonus
      if (response.data.course_completed) {
        setTimeout(() => {
          toast.success(
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              <span>Congratulations! Course completed! +100 bonus points!</span>
            </div>,
            { duration: 6000 }
          );
        }, 2500);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Already completed or not enrolled');
      } else {
        toast.error('Failed to mark lesson complete');
      }
    } finally {
      setIsCompleting(false);
    }
  };

  // Purchase individual lesson via PayPal
  const handlePurchaseLesson = async () => {
    if (isPurchasing) return;
    
    // Check if declaration exists and needs acceptance
    if (courseDeclaration && !hasAcceptedDeclaration) {
      setDeclarationAccepted(false);
      setShowDeclarationModal(true);
      return;
    }
    
    // No declaration or already accepted - proceed with purchase
    proceedWithLessonPurchase();
  };

  // Video control handlers
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = percent * videoRef.current.duration;
    }
  };

  const handleVolumeToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleSpeedChange = (speed) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
      setPlaybackSpeed(speed);
    }
  };

  const handleFullscreen = () => {
    const container = document.getElementById('video-container');
    if (container.requestFullscreen) {
      container.requestFullscreen();
    } else if (container.webkitRequestFullscreen) {
      container.webkitRequestFullscreen();
    }
  };

  const skip = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
    }
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Secure file view/download handler - uses presigned URLs for R2 files
  const handleSecureFileView = useCallback(async (file) => {
    const isPdf = file.file_type === 'pdf' || file.url?.endsWith('.pdf');
    const isAudio = file.file_type === 'audio';
    
    try {
      // Get proper URL for the file (handles R2 presigned URLs)
      const fileUrl = await getR2MediaUrl(file.url, courseId);
      
      if (!fileUrl) {
        toast.error('Failed to get file URL');
        return;
      }

      if (isPdf) {
        // For PDFs, open in Google Docs viewer or directly
        window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=false`, '_blank');
      } else {
        // For video/audio, open in new tab with a simple player
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>${file.file_name || 'Media File'}</title>
              <style>
                body { margin: 0; background: #000; display: flex; align-items: center; justify-content: center; min-height: 100vh; }
                video, audio { max-width: 100%; max-height: 100vh; }
              </style>
            </head>
            <body>
              <${isAudio ? 'audio' : 'video'} controls autoplay preload="metadata" src="${fileUrl}"></${isAudio ? 'audio' : 'video'}>
            </body>
            </html>
          `);
        }
      }
      
      // Mark file as opened (per-user tracking)
      if (file.id && !openedFiles.includes(file.id)) {
        try {
          await api.post(`/lessons/${lessonId}/files/${file.id}/open`);
          setOpenedFiles(prev => [...prev, file.id]);
        } catch (error) {
          console.error('Failed to mark file as opened:', error);
          // Non-critical error, don't show toast
        }
      }
    } catch (error) {
      console.error('Error viewing file:', error);
      toast.error('Failed to load file');
    }
  }, [courseId, lessonId, openedFiles]);

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
    controlsTimeout.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  const handleWaiting = () => setIsBuffering(true);
  const handlePlaying = () => setIsBuffering(false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#163329' }} />
      </div>
    );
  }

  // Show expired enrollment screen
  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="text-center max-w-md mx-auto p-6">
          <div 
            className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ backgroundColor: '#FEE2E2' }}
          >
            <AlertTriangle className="h-10 w-10" style={{ color: '#DC2626' }} />
          </div>
          <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
            Course Access Expired
          </h2>
          <p className="mb-6" style={{ color: '#5C635A' }}>
            Your 3-month access period for this course has ended. Please contact the administrator to extend your enrollment.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              onClick={() => navigate(`/courses/${courseId}`)}
              className="rounded-full"
            >
              View Course
            </Button>
            <Button
              onClick={() => navigate('/student')}
              className="rounded-full"
              
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while fetching media URL
  if (isLoadingMedia) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-white mx-auto mb-4" />
          <p className="text-gray-400">Loading media...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: '#1a1a1a' }}>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(`/courses/${courseId}`)}
            className="text-white hover:bg-gray-800"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-white font-semibold">{lesson?.title}</h1>
            <p className="text-gray-400 text-sm">{course?.title}</p>
          </div>
        </div>
        <Badge variant="outline" className="text-white border-gray-600">
          {lesson?.duration || 0} min
        </Badge>
      </div>

      {/* Media Player Section */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Locked Lesson Overlay - shown when user doesn't have access */}
        {!hasLessonAccess && (
          <div className="mb-6 p-6 rounded-xl text-center" style={{ backgroundColor: '#1F1F1F', border: '1px solid #333' }}>
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-full" style={{ backgroundColor: '#333' }}>
                <Lock className="h-10 w-10 text-amber-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">This Lesson is Locked</h3>
                <p className="text-gray-400 mb-4">
                  {isLessonPurchasable 
                    ? <>Unlock this lesson for <Usd value={lessonPrice} /> to access all content.</>
                    : 'Purchase the full course to access this lesson.'}
                </p>
                {isLessonPurchasable && <PriceCaption className="mb-4" />}
              </div>
              {isLessonPurchasable && lessonPrice > 0 && (
                <Button
                  onClick={handlePurchaseLesson}
                  disabled={isPurchasing}
                  className="px-6 py-3 text-lg font-semibold rounded-full"
                  style={{ backgroundColor: '#0070ba', color: 'white' }}
                >
                  {isPurchasing ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-5 w-5 mr-2" />
                      Unlock for <Usd value={lessonPrice} />
                    </>
                  )}
                </Button>
              )}
              {!isLessonPurchasable && (
                <Button
                  onClick={() => navigate(`/courses/${courseId}`)}
                  className="px-6 py-3 text-lg font-semibold rounded-full"
                  style={{ backgroundColor: '#163329', color: 'white' }}
                >
                  View Course
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Primary Content - Numbered as 1 */}
        {hasLessonAccess && mediaUrl?.type === 'pdf' ? (
          // Enhanced PDF Viewer - Doubled size for better readability
          <div className="space-y-3">
            <h4 className="text-white font-semibold mb-3">Lesson Materials</h4>
            <div 
              className="p-3 rounded-lg"
              style={{ backgroundColor: '#E8F5E9', border: '1px solid #4CAF50' }}
            >
              <div className="flex items-start gap-3">
                <Badge style={{ backgroundColor: '#2E7D32', color: 'white', minWidth: '24px', justifyContent: 'center' }} className="text-xs font-bold flex-shrink-0">
                  1
                </Badge>
                <FileText className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#2E7D32' }} />
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-sm break-words" style={{ color: '#2E7D32' }}>
                    {lesson?.primary_file_name || lesson?.title || 'Primary PDF'}
                  </span>
                  <Badge variant="outline" style={{ borderColor: '#2E7D32', color: '#2E7D32' }} className="text-xs ml-2">
                    PDF
                  </Badge>
                </div>
              </div>
            </div>
            
            {/* Embedded PDF Viewer - Enhanced 2x Size */}
            <div 
              className="border-2 rounded-lg shadow-xl overflow-hidden"
              style={{ 
                width: '100%',
                maxWidth: '1200px',
                height: '80vh',
                minHeight: '600px',
                maxHeight: '900px',
                margin: '0 auto',
                borderColor: '#E3E1DB',
                backgroundColor: '#fff'
              }}
            >
              <iframe
                src={`https://docs.google.com/viewer?url=${encodeURIComponent(mediaUrl.url)}&embedded=true`}
                loading="eager"
                style={{ 
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title={lesson?.title || 'PDF Viewer'}
              />
            </div>
            
            {/* Info and Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
              <p className="text-sm" style={{ color: '#5C635A' }}>
                📄 Enhanced View (2x Size) • Use viewer toolbar for full-screen
              </p>
              <div className="flex gap-2">
                <a 
                  href={`https://docs.google.com/viewer?url=${encodeURIComponent(mediaUrl.url)}&embedded=false`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="h-8 px-3 rounded-md text-sm font-medium flex items-center justify-center border transition-colors hover:bg-green-50"
                  style={{ color: '#2E7D32', borderColor: '#2E7D32' }}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Open in New Tab
                </a>
              </div>
            </div>
          </div>
        ) : hasLessonAccess && mediaUrl?.type === 'youtube' ? (
          // YouTube Embed - Enhanced 2x Size
          <div className="space-y-3">
            <div 
              className="flex items-center justify-between p-3 rounded-lg"
              style={{ backgroundColor: '#E8F5E9', border: '1px solid #4CAF50' }}
            >
              <div className="flex items-center gap-3">
                <Badge style={{ backgroundColor: '#2E7D32', color: 'white', minWidth: '24px', justifyContent: 'center' }} className="text-xs font-bold">
                  1
                </Badge>
                <Video className="h-4 w-4" style={{ color: '#2E7D32' }} />
                <span className="font-medium text-sm" style={{ color: '#2E7D32' }}>
                  {lesson?.title || 'Primary Video'}
                </span>
                <Badge style={{ backgroundColor: '#FF0000', color: 'white' }} className="text-xs">
                  YouTube
                </Badge>
              </div>
            </div>
            {/* Enhanced Video Container - 2x Size */}
            <div 
              id="video-container"
              className="relative w-full rounded-lg overflow-hidden mx-auto"
              style={{ 
                maxWidth: '1400px',
                height: '80vh',
                minHeight: '500px',
                maxHeight: '800px',
                backgroundColor: '#000' 
              }}
            >
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={`https://www.youtube.com/embed/${mediaUrl.id}?rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${window.location.origin}`}
                title={lesson?.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            {/* Info bar */}
            <div className="text-center text-sm py-2 px-4 rounded" style={{ backgroundColor: '#F0EFEA', color: '#5C635A' }}>
              <p>🎬 Enhanced View (2x Size) • Click full-screen button in player for maximum view</p>
            </div>
          </div>
        ) : hasLessonAccess && mediaUrl?.type === 'uploaded' ? (
          // Custom Video Player - Enhanced 2x Size
          <div className="space-y-3">
            <div 
              className="flex items-center justify-between p-3 rounded-lg"
              style={{ backgroundColor: '#E8F5E9', border: '1px solid #4CAF50' }}
            >
              <div className="flex items-center gap-3">
                <Badge style={{ backgroundColor: '#2E7D32', color: 'white', minWidth: '24px', justifyContent: 'center' }} className="text-xs font-bold">
                  1
                </Badge>
                <Video className="h-4 w-4" style={{ color: '#2E7D32' }} />
                <span className="font-medium text-sm" style={{ color: '#2E7D32' }}>
                  {lesson?.title || 'Primary Video'}
                </span>
                <Badge style={{ backgroundColor: '#163329', color: 'white' }} className="text-xs">
                  Video
                </Badge>
              </div>
            </div>
            {/* Enhanced Video Container - 2x Size */}
            <div 
              id="video-container"
              className="relative w-full rounded-lg overflow-hidden bg-black mx-auto"
              style={{ 
                maxWidth: '1400px',
                minHeight: '500px'
              }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => isPlaying && setShowControls(false)}
          >
            <video
              ref={videoRef}
              className="w-full"
              src={mediaUrl.url}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onWaiting={handleWaiting}
              onPlaying={handlePlaying}
              onEnded={() => setIsPlaying(false)}
              preload="metadata"
              playsInline
              style={{ maxHeight: '80vh', minHeight: '500px' }}
            >
              Your browser does not support the video tag.
            </video>

            {/* Buffering Indicator */}
            {isBuffering && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                <Loader2 className="h-12 w-12 animate-spin text-white" />
              </div>
            )}

            {/* Play Button Overlay */}
            {!isPlaying && !isBuffering && (
              <div 
                className="absolute inset-0 flex items-center justify-center cursor-pointer"
                onClick={handlePlayPause}
              >
                <div className="w-20 h-20 rounded-full bg-white bg-opacity-90 flex items-center justify-center">
                  <Play className="h-10 w-10 text-gray-900 ml-1" />
                </div>
              </div>
            )}

            {/* Video Controls */}
            <motion.div 
              className="absolute bottom-0 left-0 right-0 px-4 pb-4 pt-16"
              style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.9))' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: showControls ? 1 : 0 }}
              transition={{ duration: 0.2 }}
            >
              {/* Progress Bar */}
              <div 
                className="w-full h-1 bg-gray-600 rounded-full cursor-pointer mb-4 group"
                onClick={handleSeek}
              >
                <div 
                  className="h-full bg-red-500 rounded-full relative"
                  style={{ width: `${progress}%` }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>

              {/* Controls Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Play/Pause */}
                  <button onClick={handlePlayPause} className="text-white hover:text-gray-300">
                    {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
                  </button>

                  {/* Skip Backward */}
                  <button onClick={() => skip(-10)} className="text-white hover:text-gray-300">
                    <SkipBack className="h-5 w-5" />
                  </button>

                  {/* Skip Forward */}
                  <button onClick={() => skip(10)} className="text-white hover:text-gray-300">
                    <SkipForward className="h-5 w-5" />
                  </button>

                  {/* Volume */}
                  <button onClick={handleVolumeToggle} className="text-white hover:text-gray-300">
                    {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                  </button>

                  {/* Time */}
                  <span className="text-white text-sm">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  {/* Playback Speed */}
                  <select
                    value={playbackSpeed}
                    onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                    className="bg-transparent text-white text-sm border border-gray-600 rounded px-2 py-1"
                  >
                    <option value="0.5" className="bg-gray-900">0.5x</option>
                    <option value="0.75" className="bg-gray-900">0.75x</option>
                    <option value="1" className="bg-gray-900">1x</option>
                    <option value="1.25" className="bg-gray-900">1.25x</option>
                    <option value="1.5" className="bg-gray-900">1.5x</option>
                    <option value="1.75" className="bg-gray-900">1.75x</option>
                    <option value="2" className="bg-gray-900">2x</option>
                  </select>

                  {/* Fullscreen */}
                  <button onClick={handleFullscreen} className="text-white hover:text-gray-300">
                    <Maximize className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
            {/* Info bar */}
            <div className="text-center text-sm py-2 px-4 rounded" style={{ backgroundColor: '#F0EFEA', color: '#5C635A' }}>
              <p>🎬 Enhanced View (2x Size) • Click full-screen button for maximum view</p>
            </div>
          </div>
        ) : (
          // No media available - show helpful message
          <div 
            className="text-center py-16 rounded-lg"
            style={{ backgroundColor: '#1a1a1a' }}
          >
            <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-yellow-500" />
            <h3 className="text-white text-xl font-semibold mb-2">No Content Available</h3>
            <p className="text-gray-400 mb-4">
              The teacher hasn't uploaded any video or PDF for this lesson yet.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate(`/courses/${courseId}`)}
              className="text-white border-gray-600 hover:bg-gray-800"
            >
              Back to Course
            </Button>
          </div>
        )}

        {/* Lesson Info */}
        <Card className="mt-6" style={{ backgroundColor: '#1a1a1a', borderColor: '#333' }}>
          <CardHeader>
            <CardTitle className="text-white" style={{ fontFamily: 'Spectral, serif' }}>
              {lesson?.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-400">{lesson?.description}</p>
            
            {/* Additional Lesson Files - Numbered starting from 2 */}
            {lesson?.files && lesson.files.length > 0 && (
              <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: '#252525' }}>
                <h4 className="text-white font-semibold mb-3">Additional Materials</h4>
                
                {/* Search box for filtering files */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search files by name..."
                    value={fileSearchQuery}
                    onChange={(e) => setFileSearchQuery(e.target.value)}
                    className="pl-9 bg-gray-800 border-gray-600 text-white placeholder-gray-500"
                    data-testid="file-search-input"
                  />
                </div>
                
                {/* Scrollable file list container */}
                <ScrollArea className="max-h-[320px] pr-2" style={{ maxHeight: '320px' }}>
                  <div className="space-y-2">
                    {lesson.files
                      .filter(file => {
                        if (!fileSearchQuery.trim()) return true;
                        const fileName = (file.file_name || '').toLowerCase();
                        return fileName.includes(fileSearchQuery.toLowerCase());
                      })
                      .map((file, index) => {
                    const isPdf = file.file_type === 'pdf' || file.url?.endsWith('.pdf');
                    const isAudio = file.file_type === 'audio';
                    const isVideo = file.file_type === 'video' || (!isPdf && !isAudio);
                    
                    // Format file size
                    const formatFileSize = (bytes) => {
                      if (!bytes) return null;
                      return `${(bytes / 1048576).toFixed(1)} MB`;
                    };
                    // Format duration
                    const formatDuration = (seconds) => {
                      if (!seconds) return null;
                      const mins = Math.floor(seconds / 60);
                      const secs = String(Math.floor(seconds % 60)).padStart(2, '0');
                      return `${mins}:${secs}`;
                    };
                    const fileSize = formatFileSize(file.file_size);
                    const fileDuration = formatDuration(file.duration);
                    
                    // Check if this file has been opened by the user
                    const isOpened = file.id && openedFiles.includes(file.id);
                    // Sky blue style for opened files, green for unopened
                    const cardBgColor = isOpened ? '#E1F5FE' : '#E8F5E9';
                    const cardBorderColor = isOpened ? '#29B6F6' : '#4CAF50';
                    const accentColor = isOpened ? '#0288D1' : '#2E7D32';
                    
                    return (
                      <div 
                        key={file.id || index}
                        className="p-3 rounded-lg"
                        style={{ backgroundColor: cardBgColor, border: `1px solid ${cardBorderColor}` }}
                      >
                        <div className="flex items-start gap-3">
                          <Badge style={{ backgroundColor: accentColor, color: 'white', minWidth: '24px', justifyContent: 'center' }} className="text-xs font-bold flex-shrink-0">
                            {index + 2}
                          </Badge>
                          {isPdf ? (
                            <FileText className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: accentColor }} />
                          ) : isAudio ? (
                            <Music className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: accentColor }} />
                          ) : (
                            <Video className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: accentColor }} />
                          )}
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-sm break-words" style={{ color: accentColor }}>
                              {file.file_name || `File ${index + 2}`}
                            </span>
                            <Badge variant="outline" style={{ borderColor: accentColor, color: accentColor }} className="text-xs ml-2">
                              {isPdf ? 'PDF' : isAudio ? 'Audio' : 'Video'}
                            </Badge>
                            {isOpened && (
                              <Badge className="text-xs ml-1" style={{ backgroundColor: '#29B6F6', color: 'white' }}>
                                Viewed
                              </Badge>
                            )}
                            {/* File size and duration subtitle */}
                            {(fileSize || fileDuration) && (
                              <p className="text-xs mt-1" style={{ color: '#5C635A' }}>
                                {fileSize}{fileSize && fileDuration ? ' • ' : ''}{fileDuration}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: cardBorderColor }}>
                          <button
                            onClick={() => handleSecureFileView(file)}
                            className="flex-1 h-8 px-3 rounded-md text-sm font-medium flex items-center justify-center border transition-colors hover:bg-green-50 cursor-pointer"
                            style={{ color: accentColor, borderColor: accentColor, backgroundColor: 'transparent' }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </ScrollArea>
                
                {/* Show message if search has no results */}
                {fileSearchQuery.trim() && lesson.files.filter(file => {
                  const fileName = (file.file_name || '').toLowerCase();
                  return fileName.includes(fileSearchQuery.toLowerCase());
                }).length === 0 && (
                  <p className="text-gray-400 text-sm text-center py-4">
                    No files match "{fileSearchQuery}"
                  </p>
                )}
              </div>
            )}
            
            <div className="mt-6 flex items-center gap-4 flex-wrap">
              <Button
                onClick={() => navigate(`/courses/${courseId}`)}
                variant="outline"
                className="rounded-full text-white border-gray-600 hover:bg-gray-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Course
              </Button>
              <Button
                className="rounded-full"
                style={{ backgroundColor: isCompleted ? '#22C55E' : '#D86B47', color: 'white' }}
                onClick={markLessonComplete}
                disabled={isCompleting || isCompleted}
              >
                {isCompleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Completing...
                  </>
                ) : isCompleted ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Completed!
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark as Complete
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Video Optimization Tips */}
        <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: '#1a1a1a', borderColor: '#333' }}>
          <p className="text-gray-400 text-sm">
            <strong className="text-white">Tip:</strong> For the best viewing experience, use a stable internet connection. 
            You can adjust playback speed using the dropdown in the video controls.
          </p>
        </div>
      </div>

      {/* Declaration Modal for Lesson Purchase */}
      <Dialog open={showDeclarationModal} onOpenChange={setShowDeclarationModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              Acknowledgment Declaration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm" style={{ color: '#5C635A' }}>
              Before purchasing this lesson, please read and accept the following declaration:
            </p>
            
            {courseDeclaration && (
              <div 
                className="max-h-60 overflow-y-auto p-4 rounded-lg border text-sm whitespace-pre-wrap"
                style={{ borderColor: '#E3E1DB', backgroundColor: '#FAF9F6', color: '#163329' }}
              >
                {courseDeclaration.text}
              </div>
            )}
            
            <div className="flex items-start space-x-3 pt-2">
              <Checkbox
                id="lesson-declaration-accept"
                checked={declarationAccepted}
                onCheckedChange={setDeclarationAccepted}
                data-testid="lesson-declaration-checkbox"
              />
              <Label 
                htmlFor="lesson-declaration-accept" 
                className="text-sm cursor-pointer leading-tight"
                style={{ color: '#163329' }}
              >
                I have read and accept this declaration
              </Label>
            </div>
            
            <div className="flex gap-2 justify-end pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowDeclarationModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAcceptDeclaration}
                disabled={!declarationAccepted || isAcceptingDeclaration}
                data-testid="lesson-accept-declaration-button"
              >
                {isAcceptingDeclaration ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  'Accept & Continue to Payment'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LessonViewPage;
